import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    inject,
    input,
    output,
    signal,
    viewChild
} from '@angular/core';
import {WjNavModule, WjTreeView} from '@mescius/wijmo.angular2.nav';
import {FormatNodeEventArgs, TreeNode, TreeView} from '@mescius/wijmo.nav';
import {createElement} from '@mescius/wijmo';
import {AppGridViewService, StoredGridLayoutTreeItem} from '../../service/app-grid-view.service';
import {DynamicComponentBase, NgDynamicComponentsService} from '../../service/ng-dynamic-components.service';
import {toMerged} from 'es-toolkit';
import {AppConstants} from '../../app.constants';
import {Alerts, DialogButton} from '../../lib/alerts';

export type GridViewEditorMode = 'add' | 'view';

@Component({
    selector: 'app-account-grid-view-editor',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        WjNavModule
    ],
    templateUrl: './account-grid-view-editor.component.html',
    styleUrl: './account-grid-view-editor.component.scss'
})
export class AccountGridViewEditorComponent extends DynamicComponentBase implements AfterViewInit {

    // Local Storage key from which the tree is loaded and saved
    storageKey = input(AppConstants.STORAGE_TREE_KEY)
    mode = input<GridViewEditorMode>('view');
    newItemKey = input<string|undefined>();

    acceptedItem = output<StoredGridLayoutTreeItem>()

    protected readonly root: StoredGridLayoutTreeItem;
    protected readonly rootArray: (StoredGridLayoutTreeItem)[];

    protected selectedNode = signal<TreeNode | null>(null);
    protected selectedTreeItem = computed(() => this.selectedNode()?.dataItem as StoredGridLayoutTreeItem | null);
    protected selectedNodeIsLeaf = computed(() => this.selectedTreeItem()?.isFolder === false);
    protected selectedNodeIsRoot = computed(() => this.selectedTreeItem() === this.root);

    private readonly treeView = viewChild.required<WjTreeView>('theTree')
    private readonly appGridViewSvc = inject(AppGridViewService);

    constructor() {
        super();
        this.root = this.appGridViewSvc.loadGridLayoutTree(this.storageKey());
        this.rootArray = [this.root];
    }

    override ngAfterViewInit(): void {
        const theTree = this.treeView();
        theTree.formatItem.addHandler((s: TreeView, event: FormatNodeEventArgs) => {
            const item = event.dataItem as StoredGridLayoutTreeItem;
            const isFolder = item.isFolder;
            const iconClass = isFolder ? 'fa-folder' : 'fa-table';
            const imgEl = createElement(`<i class="far ${iconClass}"></i>`);
            const firstChild = event.element.firstElementChild;
            if (firstChild) {
                event.element.insertBefore(imgEl, firstChild);
            } else {
                event.element.appendChild(imgEl);
            }
        });
        super.ngAfterViewInit();
    }

    onTreeViewInitialized(theTree: WjTreeView) {
    }

    onNodeEditEnded(theTree: WjTreeView, event: FormatNodeEventArgs) {
        this.syncPersistentTree();
    }

    onSelectedNodeChange(theTree: WjTreeView, node: TreeNode | null) {
        this.selectedNode.set(node);
    }

    onAddFolder(theTree: WjTreeView) {
        const parent = this.getSelectedNodeItem();
        this.appGridViewSvc.addFolder(parent);
        this.syncPersistentTree();
        theTree.loadTree(true);
    }

    private getSelectedNodeItem(): StoredGridLayoutTreeItem {
        const node = this.treeView().selectedNode ?? this.treeView();
        return node.dataItem as StoredGridLayoutTreeItem;
    }

    async onRemoveItem() {
        const node = this.treeView().selectedNode;
        if (!node) return;
        const item = this.getSelectedNodeItem();
        if (item.isFolder) {
            const btn = await Alerts.alertPopup({
                header: 'Remove folder',
                body: `Are you sure you want to remove folder '${item.name}'? All items inside it will be removed as well.`,
                buttons: [DialogButton.Cancel, DialogButton.Ok]
            });
            if (btn !== DialogButton.Ok) return;
        }
        node?.remove();
        this.appGridViewSvc.cleanupLocalStorageForTreeGridLayout(this.root);
        this.syncPersistentTree();
    }

    private syncPersistentTree() {
        this.appGridViewSvc.saveGridLayoutTree(this.root, this.storageKey());
    }

    static async showDialog(ngDynCompSvc: NgDynamicComponentsService, options?: any): Promise<AccountGridViewEditorComponent> {
        const allOptions = toMerged({
            title: 'My Views',
            width: '800px',
        }, options);
        const componentRef = await ngDynCompSvc.createComponentDialog(AccountGridViewEditorComponent, allOptions);
        return componentRef.instance as AccountGridViewEditorComponent;
    }

    async onShareLink() {
        const item = this.getSelectedNodeItem();
        const key = item.reference;
        if (!key) return;
        const layout = this.appGridViewSvc.loadGridLayout(key);
        await this.appGridViewSvc.showShareGridViewDialog(layout);
    }

    async onAddHere() {
        const item = this.getSelectedNodeItem();

        if (item.isFolder) {
            this.appGridViewSvc.addItem(item, 'New Item', this.newItemKey()!);
        } else {
            const btn = await Alerts.alertPopup({
                header: 'Overwrite',
                body: `Are you sure you want to overwrite link '${item.name}'?`,
                buttons: [DialogButton.Cancel, DialogButton.Ok]
            });
            if (btn !== DialogButton.Ok) return;
            item.reference = this.newItemKey()!;
        }

        this.syncPersistentTree();
        this.treeView().loadTree(true);
    }

    async onOpenView() {
        try {
            const item = this.getSelectedNodeItem();
            this.acceptedItem.emit(item);
        } finally {
            this.dynPopup()?.hide();
        }
    }
}
